import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { Col, Form, Row, Table, Button } from 'react-bootstrap';
import ClipLoader from 'react-spinners/ClipLoader';
import { useState, useEffect } from 'react';
import { fetchRDADetails } from 'store/slices/Invoice/invoiceDetailSlice';
import { setTimesheetDetail } from 'store/slices/timesheet/timesheetDetailSlice';
import { BuPath } from 'constants/buPath';
import {
  displayError,
  displaySuccess,
} from 'components/common/Alert/ToastAlert';
import TimesheetActivities from './TimesheetActivities';

const TimesheetPolineSection = props => {
  const {
    activityAmount,
    timesheetDistributedLines,
    handleCheckboxChange,
    selectedCheckboxes,
    canEditActivity,
    bucketPOEnabled,
  } = props;
  const totalAmount = timesheetDistributedLines.reduce((sum, item) => {
    const amount = parseFloat(item.lineAmount);
    return sum + (isNaN(amount) ? 0 : amount);
  }, 0);

  const totalPercentage = timesheetDistributedLines.reduce((sum, item) => {
    const percentage = parseFloat(item.percentage);
    return sum + (isNaN(percentage) ? 0 : percentage);
  }, 0);
  const purchaseOrder = useSelector(state => state.purchaseOrderDetails);
  const timesheet = useSelector(state => state.timesheetDetail);
  const [loader, setLoader] = useState(false);

  const dispatch = useDispatch();

  const handleChange = (e, index, name, activityNum) => {
    let updatedTimesheet = _.cloneDeep(timesheet);

    const matchingAccounts = updatedTimesheet.timesheetAccounts.filter(
      acc => acc.activitynum === activityNum,
    );

    const accountToUpdate = matchingAccounts[index];

    if (!accountToUpdate) return;

    accountToUpdate[name] = _.isString(e.target.value)
      ? e.target.value?.trim()
      : e.target.value;

    const summary = updatedTimesheet.timesheetActivitySummaries.find(
      a => a.activitynum === activityNum,
    );

    const totalLineAmount = parseFloat(summary?.lineAmount || 0);

    if (name === 'lineAmount' && totalLineAmount) {
      const percentage = ((e.target.value / totalLineAmount) * 100).toFixed(2);
      accountToUpdate.percentage = percentage;
    } else if (name === 'percentage' && totalLineAmount) {
      const amount = ((e.target.value / 100) * totalLineAmount).toFixed(2);
      accountToUpdate.lineAmount = amount;
    }

    let totalCost = 0;
    let totalPerc = 0;
    for (let i = 0; i < updatedTimesheet?.timesheetAccounts?.length; i++) {
      if (updatedTimesheet?.timesheetAccounts[i].activitynum == activityNum) {
        totalCost += Number(updatedTimesheet?.timesheetAccounts[i].lineAmount);
        totalPerc += Number(updatedTimesheet?.timesheetAccounts[i].percentage);
      }
    }
    let remainingDifference;
    const activityNumOfSummary =
      updatedTimesheet.timesheetActivitySummaries.find(
        a => a.activitynum === activityNum,
      );
    const findLastIndex = updatedTimesheet.timesheetAccounts?.findLastIndex(
      a => a.activitynum === activityNum,
    );
    if (totalCost != activityNumOfSummary?.lineAmount && totalPerc == 100) {
      remainingDifference = Number(summary?.lineAmount - totalCost).toFixed(2);

      let finalCal = (
        Number(updatedTimesheet.timesheetAccounts[findLastIndex].lineAmount) +
        Number(remainingDifference)
      ).toFixed(2);
      updatedTimesheet.timesheetAccounts[findLastIndex].lineAmount =
        Number(finalCal);
    } else if (
      totalCost == activityNumOfSummary?.lineAmount &&
      totalPerc != 100
    ) {
      remainingDifference = Number(100 - totalPerc).toFixed(2);

      let finalCal = (
        Number(updatedTimesheet.timesheetAccounts[findLastIndex].percentage) +
        Number(remainingDifference)
      ).toFixed(2);
      updatedTimesheet.timesheetAccounts[findLastIndex].percentage =
        Number(finalCal).toFixed(2);
    }

    dispatch(setTimesheetDetail(updatedTimesheet));
  };

  const handleFindWO = (activityNum, workorder, index) => {
    setLoader(true);
    dispatch(
      fetchRDADetails({
        workorder: workorder?.trim(),
        callback: async data => {
          setLoader(false);

          if (data) {
            if (data?.error) {
              displayError(
                data?.error?.Status?.ErrorMessage ??
                  'Internal Server Error, Please try again',
              );
              return;
            }

            let updatedTimesheet = _.cloneDeep(timesheet);

            // Get filtered accounts for this activityNum
            const filteredAccounts = updatedTimesheet.timesheetAccounts.filter(
              acc => acc.activitynum === activityNum,
            );

            // Find the actual account object from global timesheet array
            const accountToUpdate = filteredAccounts[index];
            const globalIndex = updatedTimesheet.timesheetAccounts.findIndex(
              acc => acc === accountToUpdate,
            );

            if (globalIndex !== -1) {
              updatedTimesheet.timesheetAccounts[globalIndex] = {
                ...accountToUpdate,
                workorder: workorder,
                abmsProject: data?.abms_project,
                accountNmbr: data?.account_nmbr,
                projectBusinessUnit: data?.project_business_unit,
                glbu: data?.glbu,
                costComponent: data?.cost_component,
                dept: data?.dept,
                abmsActivity: data?.abms_activity,
                lineAmount: 0,
                percentage: 0,
              };

              dispatch(setTimesheetDetail(updatedTimesheet));
            } else {
              displayError('Unable to find the matching account row.');
            }
          } else {
            displayError('No Work Order Data Available');
          }
        },
      }),
    );
  };

  return (
    <div className="accountingContainer">
      {loader && (
        <ClipLoader
          color="#009cde"
          cssOverride={{
            top: '50vh',
            left: '50vw',
            position: 'absolute',
            zIndex: 1,
          }}
          size={50}
          aria-label="Loading Spinner"
          data-testid="loader"
        />
      )}
      <Table
        hover
        responsive
        striped
        bordered={false}
        style={{
          marginLeft: '0px',
          border: 'none',
          tableLayout: 'auto', // ensures dynamic column width
          width: '100%',
          wordBreak: 'break-word',
        }}
      >
        <thead>
          <tr>
            <th style={{ width: '1%' }}></th>
            <th style={{ width: '14%' }}>Work Order:</th>
            <th style={{ width: '3%' }}></th>
            <th style={{ width: '7%' }}>GLBU:</th>
            <th style={{ width: '9%' }}>Department:</th>
            <th style={{ width: '10%' }}>Account Number:</th>
            <th style={{ width: '7%' }}>Cost Component:</th>
            <th style={{ width: '7%' }}>ABMS Activity</th>
            <th style={{ width: '11%' }}>Project ID:</th>
            <th style={{ width: '10%' }}>PCBU: </th>
            <th style={{ width: '8%' }}>Per(%)</th>
            <th style={{ width: '9%' }}>Amount ($)</th>
          </tr>
        </thead>
        <tbody>
          {timesheetDistributedLines &&
            timesheetDistributedLines.map((item, index) => {
              return (
                <>
                  <tr key={index} className="table-hover-row">
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedCheckboxes?.includes(index)}
                        onChange={e =>
                          handleCheckboxChange(
                            item.activitynum,
                            index,
                            e.target.checked,
                          )
                        }
                        style={{ width: '24px' }}
                        disabled={bucketPOEnabled}
                      />
                    </td>
                    <td>
                      <Form.Control
                        className="customSelect"
                        type="text"
                        value={item.workorder}
                        name="workorder"
                        onChange={e =>
                          handleChange(e, index, 'workorder', item.activitynum)
                        }
                        disabled={bucketPOEnabled}
                      />
                    </td>
                    <td
                      style={{
                        display: 'flex',
                        borderTop: 'none',
                      }}
                    >
                      <Button
                        className="rdabtn"
                        variant="primary"
                        onClick={() =>
                          handleFindWO(item.activitynum, item.workorder, index)
                        }
                        disabled={bucketPOEnabled}
                        style={{
                          padding: '0',
                          height: '40px',
                          marginTop: '2px',
                        }}
                      >
                        Find WO
                      </Button>
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={item.glbu}
                        name="glbu"
                        className="customSelect"
                        onChange={e =>
                          handleChange(e, index, 'glbu', item.activitynum)
                        }
                        disabled={bucketPOEnabled}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={item.dept}
                        name="dept"
                        className="customSelect"
                        onChange={e =>
                          handleChange(e, index, 'dept', item.activitynum)
                        }
                        disabled={bucketPOEnabled}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={item.accountNmbr}
                        name="accountNmbr"
                        className="customSelect"
                        onChange={e =>
                          handleChange(
                            e,
                            index,
                            'accountNmbr',
                            item.activitynum,
                          )
                        }
                        disabled={bucketPOEnabled}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={item.costComponent}
                        name="costComponent"
                        className="customSelect"
                        onChange={e =>
                          handleChange(
                            e,
                            index,
                            'costComponent',
                            item.activitynum,
                          )
                        }
                        disabled={bucketPOEnabled}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={item.abmsActivity}
                        name="abmsActivity"
                        className="customSelect"
                        onChange={e =>
                          handleChange(
                            e,
                            index,
                            'abmsActivity',
                            item.activitynum,
                          )
                        }
                        disabled={bucketPOEnabled}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={item.abmsProject}
                        className="customSelect"
                        onChange={e =>
                          handleChange(
                            e,
                            index,
                            'abmsProject',
                            item.activitynum,
                          )
                        }
                        disabled={bucketPOEnabled}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={item.projectBusinessUnit}
                        name="projectBusinessUnit"
                        className="customSelect"
                        onChange={e =>
                          handleChange(
                            e,
                            index,
                            'projectBusinessUnit',
                            item.activitynum,
                          )
                        }
                        disabled={bucketPOEnabled}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        step=".01"
                        value={item.percentage}
                        name="percentage"
                        className="customSelect"
                        onChange={e =>
                          handleChange(e, index, 'percentage', item.activitynum)
                        }
                        disabled={bucketPOEnabled}
                        //   onBlur={e => handleBlur(e, index, 'percentage')}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        step=".01"
                        pattern="[0-9]*[.,]?[0-9]+"
                        value={item.lineAmount}
                        name="lineAmount"
                        className="customSelect"
                        onChange={e =>
                          handleChange(e, index, 'lineAmount', item.activitynum)
                        }
                        disabled={bucketPOEnabled}
                        //   onBlur={e => handleBlur(e, index, 'lineAmount')}
                      />
                    </td>
                  </tr>
                </>
              );
            })}
        </tbody>
      </Table>
      <div style={{ float: 'right', width: '18%' }}>
        <table>
          <tbody>
            <tr>
              <td>
                <Form.Group>
                  <Form.Label>Total Percentage (%) </Form.Label>
                  <Form.Control
                    className="customSelect"
                    value={totalPercentage.toFixed(2)}
                    disabled
                  />
                </Form.Group>
              </td>
              <td>
                <Form.Group>
                  <Form.Label>Total Amount ($) </Form.Label>
                  <Form.Control
                    className="customSelect"
                    value={totalAmount.toFixed(2)}
                    disabled
                  />
                </Form.Group>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TimesheetPolineSection;
